import { AxiosHeaders } from 'axios';
import { Involvement } from '../graphql/generated';
import { sofRestApi, sofRestAttachmentsApi } from './sofRestApi';

export const statementOfFactsApi = sofRestApi.injectEndpoints({
  endpoints: (build) => ({
    authenticateInvitee: build.query<
      string,
      { portCallId: string; uuid: string; collaboratorType: string }
    >({
      query: ({ portCallId, uuid, collaboratorType }) => ({
        url: `/api/public/invited/statement-of-facts/${portCallId}?inviteId=${uuid}&collaboratorType=${collaboratorType}`,
        method: 'GET',
        headers: { Authorization: '' }
      })
    }),
    fetchInvolvements: build.query<Involvement[], void>({
      query: () => ({
        url: `/api/private/ref-data/involvements`,
        method: 'GET'
      })
    }),
    inviteCollaborators: build.mutation<
      void,
      {
        portCallId: string | number;
        data: {
          involvement: Involvement;
          email: string;
          vesselName: string;
          voyageNumber: string;
          portName: string;
          countryName: string;
          eta: string;
          timezoneId: string;
          activities: string;
          cargoes: string;
        };
      }
    >({
      query: ({ portCallId, data }) => ({
        url: `/api/private/invites/port-calls/${portCallId}`,
        method: 'POST',
        data
      })
    }),
    resendMasterInvite: build.mutation<void, { portCallId: string | number }>({
      query: ({ portCallId }) => ({
        url: `/api/private/invites/port-calls/${portCallId}/resend`,
        method: 'POST'
      })
    })
  }),
  overrideExisting: true
});

export const {
  useAuthenticateInviteeQuery,
  useFetchInvolvementsQuery,
  useLazyFetchInvolvementsQuery,
  useInviteCollaboratorsMutation,
  useResendMasterInviteMutation
} = statementOfFactsApi;

export const authenticateInviteeMatcher =
  statementOfFactsApi.endpoints.authenticateInvitee.matchFulfilled;

export const statementOfFactsAttachmentsApi =
  sofRestAttachmentsApi.injectEndpoints({
    endpoints: (build) => ({
      uploadAttachment: build.mutation<
        void,
        { portCallId: string; data: FormData }
      >({
        query: ({ portCallId, data }) => ({
          url: `/api/private/statement-of-facts/${portCallId}/attachments`,
          method: 'POST',
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          data
        })
      }),
      deleteAttachment: build.mutation<
        void,
        { portCallId: string; fileId: string }
      >({
        query: ({ portCallId, fileId }) => ({
          url: `/api/private/statement-of-facts/${portCallId}/attachments/${fileId}`,
          method: 'DELETE'
        })
      }),
      uploadSofEventAttachment: build.mutation<
        void,
        {
          portCallId: string | number;
          eventId: string | number;
          data: FormData;
        }
      >({
        query: ({ portCallId, eventId, data }) => ({
          url: `/api/private/statement-of-facts/${portCallId}/events/${eventId}/attachments`,
          method: 'POST',
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          data
        })
      }),
      deleteSofEventAttachment: build.mutation<
        void,
        {
          portCallId: string | number;
          eventId: string | number;
          attachmentId: string | number;
        }
      >({
        query: ({ portCallId, eventId, attachmentId }) => ({
          url: `/api/private/statement-of-facts/${portCallId}/events/${eventId}/attachments/${attachmentId}`,
          method: 'DELETE'
        })
      }),
      downloadSOF: build.query<
        { document: Blob; contentDisposition: string | undefined },
        { portCallId: string; code: string; actorId: string }
      >({
        query: ({ portCallId, code, actorId }) => ({
          url: `/api/private/statement-of-facts/download/${portCallId}`,
          method: 'GET',
          params: {
            code,
            actorId
          }
        }),
        transformResponse: async (
          response: Promise<Blob>,
          meta: { headers: AxiosHeaders }
        ) => {
          return {
            document: await response,
            contentDisposition: meta.headers
              .get('Content-Disposition')
              ?.toString()
          };
        }
      })
    }),
    overrideExisting: true
  });

export const {
  useUploadAttachmentMutation,
  useDeleteAttachmentMutation,
  useUploadSofEventAttachmentMutation,
  useDeleteSofEventAttachmentMutation,
  useDownloadSOFQuery,
  useLazyDownloadSOFQuery
} = statementOfFactsAttachmentsApi;
